import React from "react";
import PropTypes from "prop-types";
import Breadcrumbs from "@mui/material/Breadcrumbs";
// import Link from "@mui/material/Link";
import { Link } from "react-router-dom";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// Reusable Breadcrumbs component using MUI v5
// Features:
// - items: [{ label, href, icon?, className }]
// - maxItems: number of items to show before collapsing into a menu (default 3)
// - LinkComponent: pass react-router's Link if you want client-side routing
// - separator: custom separator
// - responsive collapse using maxItems and breakpoint

const CollapsedMenu = ({ collapsedItems, LinkComponent, onClose }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => {
    setAnchorEl(null);
    if (onClose) onClose();
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleOpen}
        aria-label={`open ${collapsedItems.length} hidden breadcrumbs`}
      >
        <KeyboardArrowDownIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {collapsedItems.map((it, idx) => (
          <MenuItem
            key={`${it.label}-${idx}`}
            onClick={handleClose}
            component={it.href ? LinkComponent || "a" : "div"}
            href={it.href}
            to={it.href}
          >
            {it.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

CollapsedMenu.propTypes = {
  collapsedItems: PropTypes.array.isRequired,
  LinkComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  onClose: PropTypes.func,
};

export default function ReusableBreadcrumbs({
  items,
  maxItems = 3,
  separator = (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_2367_6908)">
        <path
          d="M6 4L10 8L6 12"
          stroke="#667085"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_2367_6908">
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  ),
  LinkComponent = null,
  sx = {},
  collapseBelow = "sm",
  className = "",
}) {
  const theme = useTheme();
  const isCollapsed = useMediaQuery(theme.breakpoints.down(collapseBelow));

  if (!items || !items.length) return null;

  // If responsive and screen small, reduce visible items to 2
  const effectiveMax = isCollapsed
    ? Math.max(2, Math.min(2, maxItems))
    : maxItems;

  const showCollapse = items.length > effectiveMax;
  let visibleItems = items;
  let collapsed = [];

  if (showCollapse) {
    // show first item, collapsed middle, and last (like Material UI behavior)
    visibleItems = [items[0], items[items.length - 1]];
    collapsed = items.slice(1, items.length - 1);
  }

  const renderLink = (item, isLast) => {
    const Comp = item.href ? LinkComponent || Link : Typography;
    const commonProps = {};
    if (item.href) {
      commonProps.to = item.href;
      if (LinkComponent) commonProps.to = item.href;
    }

    if (isLast) {
      return (
        <Typography
          color="text.primary"
          key={item.label}
          className={`breadCrumb-item-text breadCrumb-last-item-text ${item.className ?? ""}`}
          onClick={item.onClick}
        >
          {item.label}
        </Typography>
      );
    }

    if (item.href) {
      return (
        <Comp
          key={item.label}
          underline="hover"
          variant="body2"
          {...commonProps}
          className={`breadCrumb-item-text breadCrumb-item-link ${
            item.className ?? ""
          }`}
          onClick={item.onClick}
        >
          {item.label}
        </Comp>
      );
    }

    return (
      <Typography
        key={item.label}
        variant="body2"
        className={`breadCrumb-item-text ${item.className ?? ""}`}
      >
        {item.label}
      </Typography>
    );
  };

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", ...sx }}
      className={`breadCrumbs-box-container ${className}`}
    >
      <Breadcrumbs
        separator={separator}
        aria-label="breadcrumb"
        className="breadCrumbs-container"
      >
        {showCollapse ? (
          <>
            {renderLink(visibleItems[0], false)}
            <CollapsedMenu
              collapsedItems={collapsed}
              LinkComponent={LinkComponent}
            />
            {renderLink(visibleItems[1], true)}
          </>
        ) : (
          items.map((item, idx) => renderLink(item, idx === items.length - 1))
        )}
      </Breadcrumbs>
    </Box>
  );
}

ReusableBreadcrumbs.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string,
      icon: PropTypes.node,
    })
  ).isRequired,
  maxItems: PropTypes.number,
  separator: PropTypes.node,
  LinkComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  sx: PropTypes.object,
  collapseBelow: PropTypes.string,
};

// ------------------ Example usage ------------------
//
// import ReusableBreadcrumbs from './ReusableBreadcrumbs';
// import { Link as RouterLink } from 'react-router-dom';
//
// const items = [
//   { label: 'Home', href: '/' },
//   { label: 'Categories', href: '/categories' },
//   { label: 'Electronics', href: '/categories/electronics' },
//   { label: 'Headphones', href: '/categories/electronics/headphones' },
// ];
//
// <ReusableBreadcrumbs
//   items={items}
//   maxItems={3}
//   separator="›"
//   LinkComponent={RouterLink}
//   collapseBelow="sm"
// />

// Notes:
// - Pass a Router Link (from react-router-dom) to LinkComponent for SPA navigation.
// - The component collapses middle items into a menu when items.length > maxItems.
// - You can customize styles via the sx prop.
